import { useEffect, useState, type ReactNode } from "react";
import { AppStartupLoader } from "./AppStartupLoader";

const MIN_DISPLAY_MS = 1400;
const FADE_OUT_MS = 500;

function waitForAppReady(): Promise<void> {
  if (document.readyState === "complete") {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    window.addEventListener("load", () => resolve(), { once: true });
  });
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

type AppStartupGateProps = {
  children: ReactNode;
};

export function AppStartupGate({ children }: AppStartupGateProps) {
  const [phase, setPhase] = useState<"loading" | "exiting" | "done">("loading");

  useEffect(() => {
    document.getElementById("app-splash")?.remove();
  }, []);

  useEffect(() => {
    let cancelled = false;
    let fadeTimer: number | undefined;

    void Promise.all([waitForAppReady(), delay(MIN_DISPLAY_MS)]).then(() => {
      if (cancelled) return;
      setPhase("exiting");
      fadeTimer = window.setTimeout(() => {
        if (!cancelled) setPhase("done");
      }, FADE_OUT_MS);
    });

    return () => {
      cancelled = true;
      if (fadeTimer !== undefined) window.clearTimeout(fadeTimer);
    };
  }, []);

  return (
    <>
      {children}
      {phase !== "done" && <AppStartupLoader exiting={phase === "exiting"} />}
    </>
  );
}
